import React, { useState } from 'react';

import {
    Popover,
    Stack,
    TextField,
    Button,
    Typography,
    Autocomplete,
    Tooltip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../_config/react/useAppDispatch';

import { selectChantiersByEntrepriseLabelsAndIds } from '../../../../App/core/chantier/selectors';
import { selectAllInterlocuteursLabelsAndIds } from '../../../../App/core/interlocuteur/selectors';
import { selectors as articleSelectors } from '../../../../App/core/article';
import { selectAllLabelsAndIds } from '../../../../App/core/entreprise/selectors';

import { updateFilters } from '../../../Domain/Bons/bon.slice';
import { BonFilters } from '../../../Domain/Bons/entities';
import { selectFilters } from '../../../Domain/Bons/selectors';
import { bonStatus } from "./bonStatusLabelToValue";

interface Props {
    open: boolean;
    anchorEl: Element | null;

    onCloseSearchPopover(): void;
}

function BonSearchPopover({ open, anchorEl, onCloseSearchPopover }: Props) {
    const savedFilters = useSelector(selectFilters);
    const emptyFilters = {
        fournisseur: '',
        chantier: '',
        client: '',
        interlocuteurClient: '',
        article: '',
        status: '',
    };
    const defaultValues = savedFilters ? savedFilters : emptyFilters;

    const [filters, setFilters] = useState<BonFilters>(defaultValues);
    const entreprises = useSelector(selectAllLabelsAndIds);
    const chantiers = useSelector(selectChantiersByEntrepriseLabelsAndIds(filters.client));
    const interlocuteurs = useSelector(selectAllInterlocuteursLabelsAndIds);
    const articles = useSelector(articleSelectors.selectAllArticlesLabelsAndIds);
    const dispatch = useAppDispatch();

    const isOptionEqualToValue = (option: any, value: any) => option.id === value.id || value.id === ''; // if id is empty returns true to remove warnings in console

    const getFilterValue = (options: { id: string; label: string }[], property: keyof BonFilters) => {
        return options.find((option) => option.id === filters[property]) ?? { id: '', label: '' };
    };

    const handleInputChange = (params: { property: string; value: any }) => {
        const { property, value } = params;
        if ( value ) {
            setFilters({ ...filters, [property]: value.id });
        } else {
            setFilters({ ...filters, [property]: '' });
        }
    };

    const onClickSaveFilters = () => {
        onCloseSearchPopover();
        dispatch(updateFilters(filters));
    };

    const onResetForm = () => {
        setFilters(emptyFilters);
    };

    return (
        <Popover open={open} anchorEl={anchorEl}>
            <Stack direction="column" gap={2} width={400} p={3}>
                <Typography variant="h5">Filtres</Typography>
                {/* Client */}
                <Autocomplete
                    id="client"
                    options={entreprises}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={getFilterValue(entreprises, 'client')}
                    onChange={(e, value) => handleInputChange({ property: 'client', value })}
                    renderInput={(params) => <TextField {...params} label="Client" variant="standard"/>}
                />

                {/* Chantier */}
                <Autocomplete
                    options={chantiers}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={getFilterValue(chantiers, 'chantier')}
                    onChange={(e, value) => handleInputChange({ property: 'chantier', value })}
                    renderInput={(params) => <TextField {...params} label="Chantier" variant="standard"/>}
                />

                {/* Interlocuteur */}
                <Autocomplete
                    id="interlocuteur"
                    options={interlocuteurs}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={getFilterValue(interlocuteurs, 'interlocuteurClient')}
                    onChange={(e, value) => handleInputChange({ property: 'interlocuteurClient', value })}
                    renderInput={(params) => <TextField {...params} label="Interlocuteur" variant="standard"/>}
                />

                {/* Article */}
                <Autocomplete
                    id="article"
                    options={articles}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={getFilterValue(articles, 'article')}
                    onChange={(e, value) => handleInputChange({ property: 'article', value })}
                    renderInput={(params) => <TextField {...params} label="Article" variant="standard"/>}
                />

                {/* Fournisseur */}
                <Autocomplete
                    options={entreprises}
                    isOptionEqualToValue={isOptionEqualToValue}
                    value={getFilterValue(entreprises, 'fournisseur')}
                    onChange={(e, value) => handleInputChange({ property: 'fournisseur', value })}
                    renderInput={(params) => <TextField {...params} label="Fournisseur" variant="standard"/>}
                />

                {/* Status */}
                <FormControl>
                    <InputLabel sx={{ ml: -2 }} id="select-status-label">
                        Statut
                    </InputLabel>
                    <Select
                        labelId="select-status-label"
                        value={filters.status}
                        variant="standard"
                        label="Statut"
                        onChange={(event) => setFilters({ ...filters, status: event.target.value })}
                    >
                        <MenuItem value="">
                            <em>Tout</em>
                        </MenuItem>
                        {bonStatus.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Stack direction="row" justifyContent="space-between" mt={2}>
                    <Tooltip title="Réinitialiser">
                        <Button variant="outlined" color="error" onClick={onResetForm}>
                            <RestartAlt/>
                        </Button>
                    </Tooltip>
                    <Stack direction="row" justifyContent="flex-end" gap={2}>
                        <Button onClick={onCloseSearchPopover}>Fermer</Button>
                        <Button color="primary" variant="contained" onClick={onClickSaveFilters}>
                            Enregistrer
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </Popover>
    );
}

export default BonSearchPopover;
